import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { DateTime } from 'luxon';
import { getCompanyRegistry } from './company/companyRegistrySingleton';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// --- Formatting and Payment Label Utilities ---

/**
 * Adds seconds to an `HH:mm` time string and returns normalized `HH:mm` (wraps at 24h).
 */
export function addSecondsToTime(time: string, seconds: number): string {
  try {
    const [h, m] = time.split(":").map(Number);
    const totalSeconds = h * 3600 + m * 60 + seconds;
    const daySeconds = 24 * 3600;
    const normalized = ((totalSeconds % daySeconds) + daySeconds) % daySeconds; // keep within 0..day
    const newH = Math.floor(normalized / 3600);
    const newM = Math.floor((normalized % 3600) / 60);
    return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}`;
  } catch {
    return time;
  }
}

/**
 * Format a number as a currency string using the company's currency symbol
 * (e.g., "75,00 BYN" or "75,00 Kč").
 * Falls back to "BYN" when company settings are not yet loaded.
 */
export function formatCurrency(value?: number | null): string {
  if (typeof value !== "number" || isNaN(value)) return "—";
  const symbol = getCompanyRegistry().getCurrencySymbol();
  return value.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "\u00A0" + symbol;
}

/**
 * Map payment status fields to a user-friendly label (i18n-aware).
 */
export type TFunction = (key: string, fallback: string) => string;

export function getPaymentStatusLabelI18n(is_paid?: boolean, is_overpaid?: boolean, t?: TFunction): string {
  const translate = t ?? ((_k: string, f: string) => f);
  if (is_paid) return translate("appointment.payment.paid", "Paid");
  if (is_overpaid) return translate("appointment.payment.overpaid", "Overpaid");
  return translate("appointment.payment.not_paid", "Not paid");
}

/**
 * Map prepaid status to a user-friendly label (i18n-aware).
 */
export function getPrepaidLabelI18n(prepaid_status?: boolean | number, normalizedPrepaid?: string, t?: TFunction): string {
  const translate = t ?? ((_k: string, f: string) => f);
  if (typeof normalizedPrepaid === "string") return normalizedPrepaid;
  if (typeof prepaid_status !== "undefined") {
    return prepaid_status ? translate("common.yes", "Yes") : translate("common.no", "No");
  }
  return translate("common.no", "No");
}

/**
 * Map sale bill printed status to a user-friendly label (i18n-aware).
 */
export function getSaleBillPrintedLabelI18n(is_sale_bill_printed?: boolean, normalizedSaleBillPrinted?: string, t?: TFunction): string {
  const translate = t ?? ((_k: string, f: string) => f);
  if (typeof normalizedSaleBillPrinted === "string") return normalizedSaleBillPrinted;
  if (typeof is_sale_bill_printed !== "undefined") {
    return is_sale_bill_printed ? translate("common.yes", "Yes") : translate("common.no", "No");
  }
  return translate("common.no", "No");
}

// Helper to get week start (Monday) for a given date string
export function getWeekStart(dateStr: string): string {
  const date = DateTime.fromISO(dateStr, { zone: 'UTC' });
  const day = date.weekday; // 1 = Monday, 7 = Sunday
  const diff = day === 7 ? -6 : 1 - day; // Adjust to get Monday
  const monday = date.plus({ days: diff });
  return monday.toISODate();
}

/**
 * Extract short time string (HH:mm) from a time string, which could be ISO date-time or simple HH:mm format.
 * For UI display purposes.
 */
export function formatTimeFromString(timeStr?: string): string {
  if (!timeStr) return "";
  try {
    // If it's already in HH:mm format, return as is
    if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
    // Otherwise, assume it's ISO format and parse it using salon timezone
    const timezone = getCompanyRegistry().getTimezone();
    const date = DateTime.fromISO(timeStr, { zone: 'UTC' }).setZone(timezone);
    if (!date.isValid) return "";
    return date.toFormat('HH:mm');
  } catch {
    return "";
  }
}

/**
 * Combine a date string (YYYY-MM-DD) and time string (HH:mm) into a full ISO date-time string.
 * Ensures the selected time is preserved by constructing the ISO string in UTC using salon timezone.
 */
export function formatISOFromDateTime(dateStr: string, timeStr: string): string {
  try {
    const timezone = getCompanyRegistry().getTimezone();
    const date = DateTime.fromISO(`${dateStr}T${timeStr}:00`, { zone: timezone }).toUTC();
    if (!date.isValid) throw new Error("Invalid date");
    return date.toISO().replace(/\.(\d{1,3})Z$/, ".000Z");
  } catch {
    return "";
  }
}

/**
 * Legacy function for compatibility with older code expecting ISO input.
 * Redirects to formatTimeFromString.
 */
export function formatTimeFromISO(isoDateTime?: string): string {
  return formatTimeFromString(isoDateTime);
}

/**
 * Format a duration expressed in seconds into a human-friendly string.
 * Uses English format for i18n compatibility.
 * Examples:
 *   3600 -> "1h"
 *   5400 -> "1h 30min"
 *   1800 -> "30min"
 */
export function formatDurationSeconds(seconds?: number | null): string {
  if (!seconds || !Number.isFinite(seconds)) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}min`);
  return parts.join(" ").trim() || "0min";
}

/**
 * Compute the difference in minutes between two HH:mm time strings.
 * Handles overnight spans (e.g. 23:30 → 00:15 = 45 min).
 * Returns null when either value is missing or unparseable.
 */
export function computeDurationMinutes(startTime?: string, endTime?: string): number | null {
  if (!startTime || !endTime) return null;
  try {
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    if ([sh, sm, eh, em].some((n) => !Number.isFinite(n))) return null;
    let start = sh * 60 + sm;
    let end = eh * 60 + em;
    if (end < start) end += 24 * 60; // overnight
    return end - start;
  } catch {
    return null;
  }
}

/**
 * Format a total-minutes value as a Russian-locale duration label.
 * Examples:
 *   30  → "30 мин."
 *   60  → "1 ч."
 *   90  → "1 ч. 30 мин."
 *   0   → ""
 */
export function formatDurationMinutes(totalMin: number | null | undefined): string {
  if (totalMin == null || !Number.isFinite(totalMin) || totalMin <= 0) return "";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h} ч. ${m} мин.`;
  if (h > 0) return `${h} ч.`;
  return `${m} мин.`;
}

